import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { useLocation } from "react-router";
import { useParams } from "react-router-dom";

import MapTool, { MapToolAvailability } from "@mapmycustomers/shared/enum/map/MapTool";
import MarkerShape from "@mapmycustomers/shared/enum/MarkerShape";
import MarkerSize from "@mapmycustomers/shared/enum/MarkerSize";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import { AnyEntityId, EntityType } from "@mapmycustomers/shared/types/entity";
import { CategorizedMapEntries, EntityPin } from "@mapmycustomers/shared/types/map";
import User from "@mapmycustomers/shared/types/User";
import { ModifiedMapFilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/MapFilterModel";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import { convertLatLngBoundsToPlatformBounds } from "@mapmycustomers/shared/util/geo/GeoService";
import isValidLongLat from "@mapmycustomers/shared/util/geo/isValidLongLat";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import DataViewEntityTypeCount from "@app/component/DataView/type/DataViewEntityTypeCount";
import { UserLocation } from "@app/component/map/FeaturedMap";
import useMap from "@app/component/map/FeaturedMap/utils/useMap";
import RecordStylingContext from "@app/component/RecordCard/utils/RecordStylingContext";
import IMapViewContext from "@app/component/view/types/IMapViewContext";
import BaseMapViewContext from "@app/component/view/utils/BaseMapViewContext";
import AlertBadgesType from "@app/enum/map/AlertBadgesType";
import MarkerEffect from "@app/enum/MarkerEffect";
import EntityAnnotation from "@app/scene/map/components/EntityAnnotation";
import EntityLassoTool from "@app/scene/map/components/EntityLassoTool";
import ClusterFeature from "@app/scene/map/components/features/ClusterFeature";
import EntityFeature from "@app/scene/map/components/features/EntityFeature";
import MultiPinFeature from "@app/scene/map/components/features/MultiPinFeature";
import PinLocationEntityFeature from "@app/scene/map/components/features/PinLocationEntityFeature";
import {
  BoundariesLayer,
  HeatMapLayer,
  TrafficLayer,
  UserLocationLayer,
} from "@app/scene/map/components/layers";
import ResultsModal from "@app/scene/map/components/LeadFinderMode/components/ResultsModal";
import Legends from "@app/scene/map/components/Legends";
import LoadingStatusPanel from "@app/scene/map/components/LoadingStatusPanel";
import LocationFilterCircle from "@app/scene/map/components/LocationFilterCircle";
import ContextMenu from "@app/scene/map/components/MapMode/components/ContextMenu";
import styles from "@app/scene/map/components/MapMode/MapPage.module.scss";
import CreateEditPinLegendDrawer from "@app/scene/map/components/pinLegends/CreateEditPinLegendDrawer";
import Toolbar from "@app/scene/map/components/Toolbar";
import DropPinTool from "@app/scene/map/components/tools/DropPinTool";
import LassoTool from "@app/scene/map/components/tools/LassoTool";
import MapToolInformationPanel from "@app/scene/map/components/tools/MapToolInformationPanel";
import ToolSelectionPanel from "@app/scene/map/components/ToolSelectionPanel";
import Legend from "@app/scene/map/enums/Legend";
import { fetchLassoSelection, fetchPins, initializeMapMode } from "@app/scene/map/store/actions";
import { setPinHover, setSelectedPin, showAnnotation } from "@app/scene/map/store/mapMode/actions";
import {
  getLayerLegendsState,
  getMapEntries,
  getMapViewState,
  getMapViewTool,
  getSelectedRecords,
  getVisibleBadges,
  isInitializing,
  isResultModalVisible,
  isSidebarVisible,
} from "@app/scene/map/store/selectors";
import mergeFilterModels from "@app/scene/map/utils/mergeFilterModels";
import useMapEntityUpdateListener from "@app/scene/map/utils/useMapEntityUpdateListener";
import { showEntityView } from "@app/store/entityView/actions";
import { RootState } from "@app/store/rootReducer";
import { subscribe, unsubscribe } from "@app/store/uiSync/actions";
import useMapEventHandler from "@app/util/hook/map/useMapEventHandler";
import { mapPinIdGetter } from "@app/util/map/idGetters";
import { mapEntityIdParser } from "@app/util/map/idParsers";
import defaultEntityStyleGetter from "@app/util/map/markerStyles/defaultEntityStyleGetter";

import DataViewWrapper from "../DataViewWrapper";
import UserLocationPanel from "../tools/UserLocationPanel";

import EntityActions from "./components/EntityActions";
import FocusedEntity from "./components/FocusedEntity";
import MapPoints from "./components/MapPoints";
import Sidebar from "./components/Sidebar";
import TopPanel from "./components/TopPanel";

// wait for max 500ms before triggering next fetchPins request
const boundsChangeOptions = { maxWait: 500 };

interface Props {
  initialize: typeof initializeMapMode.request;
  isInitializing: boolean;
  isSidebarVisible: boolean;
  lassoedRecords: Set<string>;
  lassoFetcher: typeof fetchLassoSelection.request;
  mapEntries: CategorizedMapEntries;
  mapViewState: MapViewState;
  onShowAnnotation: typeof showAnnotation.request;
  pinsFetcher: typeof fetchPins.request;
  resultsModalVisible: boolean;
  selectedTool?: MapTool;
  setPinHover: typeof setPinHover;
  setSelectedPin: typeof setSelectedPin;
  showEntityView: typeof showEntityView;
  subscribe: typeof subscribe;
  unsubscribe: typeof unsubscribe;
  visibleBadges: AlertBadgesType[];
}

const MapPage: React.FC<Props> = ({
  initialize,
  isInitializing,
  isSidebarVisible,
  lassoedRecords,
  lassoFetcher,
  mapEntries,
  mapViewState,
  onShowAnnotation,
  pinsFetcher,
  resultsModalVisible,
  selectedTool,
  setPinHover,
  setSelectedPin,
  showEntityView,
  subscribe,
  unsubscribe,
  visibleBadges,
}) => {
  const { map } = useMap();
  const { search } = useLocation();
  const [dataViewVisible, , hideDataView, toggleDataView] = useBoolean();
  const [multipolygon, setMultipolygon] = useState<LongLat[][]>([]);
  const { sidebarEntityId, sidebarEntityType } = useParams<{
    sidebarEntityId: string;
    sidebarEntityType: string;
  }>();

  useEffect(() => {
    map.setOptions({ clickableIcons: true });
    return () => {
      map.setOptions({ clickableIcons: false });
    };
  }, [map]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (!isSidebarVisible) {
      hideDataView();
    }
  }, [hideDataView, isSidebarVisible]);

  const entityTypeRecordsCount: DataViewEntityTypeCount = useMemo(() => {
    return Array.from(lassoedRecords)
      .map(mapEntityIdParser)
      .filter(isDefined)
      .reduce<DataViewEntityTypeCount>(
        (result, record) => ({
          ...result,
          [record.entity]: (result[record.entity] ?? 0) + 1,
        }),
        { [EntityType.COMPANY]: 0, [EntityType.DEAL]: 0, [EntityType.PERSON]: 0 }
      );
  }, [lassoedRecords]);

  useEffect(() => {
    if (map && search) {
      const urlParams = new URLSearchParams(search);
      const centerPoint = urlParams.get("center");
      if (centerPoint) {
        const coordinates = decodeURIComponent(centerPoint).split(",").map(parseFloat);
        if (isValidLongLat(coordinates as LongLat)) {
          map.setCenter({
            lat: coordinates[1],
            lng: coordinates[0],
          });
          map.setZoom(14);
        }
      }
    }
  }, [map, search]);

  useEffect(
    () => {
      if (map && sidebarEntityId && sidebarEntityType) {
        showEntityView({
          entityId: parseInt(sidebarEntityId, 10) as AnyEntityId,
          entityType: sidebarEntityType as EntityType,
        });
      }
    },
    // call action only during page initialization
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [map, showEntityView]
  );

  useEffect(() => {
    // initialization
    const bounds = map.getBounds();
    const zoom = map.getZoom();
    if (bounds && zoom) {
      pinsFetcher({
        request: {},
        viewport: {
          bounds: convertLatLngBoundsToPlatformBounds(bounds),
          zoom,
        },
      });
    }
  }, [map, pinsFetcher]);

  useMapEventHandler(
    map,
    // We don't use `idle` because it is not triggered if you stopped moving but keep mouse
    // button pressed. Hence, we use debounce to avoid calling backend too frequently.
    // However, we still use maxWait to guarantee that at least one request is sent every
    // maxWait milliseconds. This is needed to load data if user is panning continuously.
    "bounds_changed",
    useDebouncedCallback([
      function (this: google.maps.Map) {
        const bounds = this.getBounds();
        const zoom = this.getZoom();
        if (bounds && zoom && !isInitializing) {
          pinsFetcher({
            request: {},
            viewport: { bounds: convertLatLngBoundsToPlatformBounds(bounds), zoom },
          });
        }
      },
      100,
      boundsChangeOptions,
    ])
  );

  const handleReload = useCallback(() => pinsFetcher({ request: {} }), [pinsFetcher]);
  useMapEntityUpdateListener(mapViewState.visibleEntities, subscribe, unsubscribe, handleReload);

  const handleUpdateFilterModel: IMapViewContext["updateFilterModel"] = useDynamicCallback(
    (newFilterModel: ModifiedMapFilterModel) => {
      const resultingFilterModel = mergeFilterModels(mapViewState.filter, newFilterModel);
      pinsFetcher({ request: { filter: resultingFilterModel } });
    }
  );

  const handleUpdateViewState: IMapViewContext["updateViewState"] = useDynamicCallback(
    (viewState: Partial<MapViewState>) => {
      pinsFetcher({ request: viewState, updateOnly: true });
    }
  );

  const handleUpdateViewAs: IMapViewContext["updateViewAs"] = useDynamicCallback(
    (userId: undefined | User["id"]) => {
      pinsFetcher({ request: { viewAs: userId } });
    }
  );

  const context: IMapViewContext = useMemo(
    () => ({
      updateFilterModel: handleUpdateFilterModel,
      updateViewAs: handleUpdateViewAs,
      updateViewState: handleUpdateViewState,
      updateViewStateAndFetch: pinsFetcher,
      viewState: mapViewState,
    }),
    [handleUpdateFilterModel, handleUpdateViewAs, handleUpdateViewState, mapViewState, pinsFetcher]
  );

  const handlePinClick = useCallback(
    (pin) => {
      onShowAnnotation(mapPinIdGetter(pin));
    },
    [onShowAnnotation]
  );

  const handlePinDoubleClick = useCallback(
    (pin) => {
      setSelectedPin(pin);
      setPinHover(mapPinIdGetter(pin));
      showEntityView({
        entityId: pin.data.id,
        entityType: pin.data.entity,
      });
    },
    [showEntityView, setSelectedPin, setPinHover]
  );

  const availableTools = selectedTool ? MapToolAvailability.get(selectedTool) : undefined;

  const recordStylingValue = useMemo(
    () => ({
      getPinStyle: defaultEntityStyleGetter({
        areBadgesVisible: visibleBadges.length > 0,
        effect: MarkerEffect.NONE,
        markerSize: MarkerSize.LARGE,
        shape: (entityPin: EntityPin) => entityPin.data.shape ?? MarkerShape.SQUARE,
        showEntityIcon: true,
        visibleBadges,
      }),
    }),
    [visibleBadges]
  );

  return (
    <BaseMapViewContext.Provider value={context}>
      <RecordStylingContext.Provider value={recordStylingValue}>
        <div className={styles.container}>
          <FocusedEntity />
          <div className={styles.bottomContainer}>
            <Legends className={styles.legends} />
            <LoadingStatusPanel className={styles.loadingStatusPanel} />
          </div>
          <UserLocationPanel />
          <ToolSelectionPanel availableTools={availableTools} />
          <Toolbar disabled={selectedTool === MapTool.LASSO} />
          <TopPanel />
          <MapToolInformationPanel />
          {dataViewVisible && selectedTool === MapTool.LASSO && (
            <DataViewWrapper
              entityTypeRecordsCount={entityTypeRecordsCount}
              multipolygon={multipolygon}
              onHide={hideDataView}
              totalCount={lassoedRecords.size}
            />
          )}
        </div>
        <ClusterFeature clusters={mapEntries.clusters} />
        <MultiPinFeature multiPins={mapEntries.multiPins} />
        <EntityFeature
          entityPins={mapEntries.entities}
          onClick={handlePinClick}
          onDoubleClick={handlePinDoubleClick}
        />
        <EntityAnnotation
          actions={<EntityActions />}
          showGenericActions={selectedTool !== MapTool.LASSO}
        />
        <PinLocationEntityFeature />
        <LocationFilterCircle />
        <UserLocation />
        <HeatMapLayer />
        <BoundariesLayer />
        <TrafficLayer />
        <UserLocationLayer />
        <ContextMenu />
        <MapPoints />
        <LassoTool />
        <EntityLassoTool fetcher={lassoFetcher} onUpdateLasso={setMultipolygon} />
        <DropPinTool />
        <CreateEditPinLegendDrawer />
        {resultsModalVisible && <ResultsModal />}
        <Sidebar onDataViewClick={toggleDataView} />
      </RecordStylingContext.Provider>
    </BaseMapViewContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => {
  const areBadgesVisible = getLayerLegendsState(state)[Legend.ALERTS]?.visible;
  const visibleBadges = getVisibleBadges(state);

  return {
    isInitializing: isInitializing(state),
    isSidebarVisible: isSidebarVisible(state),
    lassoedRecords: getSelectedRecords(state),
    mapEntries: getMapEntries(state),
    mapViewState: getMapViewState(state),
    resultsModalVisible: isResultModalVisible(state),
    selectedTool: getMapViewTool(state),
    visibleBadges: areBadgesVisible ? visibleBadges : [],
  };
};

const mapDispatchToProps = {
  initialize: initializeMapMode.request,
  lassoFetcher: fetchLassoSelection.request,
  onShowAnnotation: showAnnotation.request,
  pinsFetcher: fetchPins.request,
  setPinHover,
  setSelectedPin,
  showEntityView,
  subscribe,
  unsubscribe,
};

export default connect(mapStateToProps, mapDispatchToProps)(MapPage);
